import React, { useEffect, useState } from 'react';
import { RotateCcw, Loader2 } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { requestRefund, myRefunds } from '../api/shop';
import { canWrite } from '../lib/canWrite';
import { fmtCredits, creditsToEur } from '../lib/currency';

/**
 * RefundRequestCard — Phase 44 · Feb 2026.
 *
 * Extracted from `ShopPage.jsx` (Phase 37A.5) so the "Richiedi un
 * rimborso" section can live in the Wallet page directly, below the
 * ledger, instead of being buried inside the Shop / "Acquista crediti"
 * page. The refund logic, endpoints and validations are IDENTICAL to
 * the original — only the mount point moved. See PRD Phase 44.
 *
 *   - Reads the user wallet balance from AuthContext (`user.wallet_balance`).
 *   - Persists via existing `POST /api/shop/refund` endpoint.
 *   - History via `GET /api/shop/refunds/me`.
 */
const RefundRequestCard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('shop');

  const balance = Number(user?.wallet_balance || 0);
  const writable = canWrite(user);
  const canRefund = balance > 0 && writable;

  const [refundAmt, setRefundAmt] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refunds, setRefunds] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { setRefunds(await myRefunds()); } catch (_e) { /* ignore */ }
    })();
  }, []);

  const submitRefund = async (e) => {
    e.preventDefault();
    if (user && user.email_verified === false) {
      toast({
        title: t('refund.verify_email_title'),
        description: t('refund.verify_email_desc'),
        variant: 'destructive',
      });
      return;
    }
    const amt = parseInt(refundAmt, 10);
    if (!Number.isFinite(amt) || amt < 1 || amt > balance) {
      toast({ title: t('refund.amount_invalid'), variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      await requestRefund(amt, refundReason);
      toast({ title: t('refund.submit_success_title'), description: t('refund.submit_success_desc') });
      setRefundAmt('');
      setRefundReason('');
      setRefunds(await myRefunds());
    } catch (err) {
      toast({
        title: t('refund.submit_error_title'),
        description:
          (err.response?.data?.detail || '').split('|').pop() ||
          t('refund.submit_error_default'),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="mt-8 rounded-2xl border border-border bg-card p-5"
      data-testid="refund-section"
    >
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <RotateCcw className="h-4 w-4 text-red-300" />
        {t('refund.section_title')}
      </h2>
      <p className="mb-3 text-[12px] leading-snug text-muted-foreground">
        <Trans i18nKey="refund.intro" ns="shop" components={{ strong: <span className="font-semibold" /> }} />
      </p>

      <form onSubmit={submitRefund} className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="number"
            step="1"
            min="1"
            max={balance}
            value={refundAmt}
            onChange={(e) => setRefundAmt(e.target.value)}
            placeholder={t('refund.amount_placeholder', { balance })}
            disabled={!canRefund}
            className="max-w-[220px]"
            data-testid="refund-amount-input"
          />
          <span className="text-xs text-muted-foreground">
            {t('refund.amount_suffix')}{' '}
            {refundAmt && t('refund.eur_hint', {
              eur: creditsToEur(Number(refundAmt || 0)).toFixed(2),
            })}
          </span>
        </div>
        <Textarea
          value={refundReason}
          onChange={(e) => setRefundReason(e.target.value.slice(0, 400))}
          placeholder={t('refund.reason_placeholder')}
          rows={2}
          disabled={!canRefund}
          data-testid="refund-reason-input"
        />
        <Button
          type="submit"
          disabled={!canRefund || busy}
          variant="outline"
          className="border-red-500/40 text-red-200 hover:bg-red-500/10"
          data-testid="refund-submit-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('refund.submit')}
        </Button>
      </form>

      {refunds.length > 0 && (
        <div className="mt-5" data-testid="refund-history">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t('refund.history_title')}
          </p>
          <ul className="divide-y divide-border/60 rounded-lg border border-border bg-background">
            {refunds.map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between px-3 py-2 text-xs"
                data-testid={`refund-row-${r.id}`}
              >
                <div>
                  <p className="text-foreground">
                    <span className="font-semibold">{fmtCredits(r.amount)}</span>
                    <span className="ml-1 text-muted-foreground">
                      (€{creditsToEur(r.amount).toFixed(2)})
                    </span>
                    {' · '}
                    {new Date(r.created_at).toLocaleDateString()}
                  </p>
                  {r.reason && (
                    <p className="line-clamp-1 text-muted-foreground">{r.reason}</p>
                  )}
                </div>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                    r.status === 'approved'
                      ? 'bg-emerald-500/20 text-emerald-300'
                      : r.status === 'declined'
                        ? 'bg-red-500/20 text-red-300'
                        : 'bg-amber-500/20 gold-text'
                  }`}
                >
                  {t(`status.${r.status}`, { defaultValue: r.status })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default RefundRequestCard;
